import type { ApolloClient } from '@apollo/client';
import {
  CountryCode,
  CurrencyCode,
} from '_graphql/__generated__/globalTypes';



export type OrganizationCurrencyInfo = {
  /** Distinct uppercase currency codes from `organizationOperationCountries` */
  allowedCurrencyCodes: Set<string>;
  /** Sorted list for Excel template dropdown */
  currencyCodesList: string[];
  /** Currency for the operating country row matching the selected `CountryCode` (iso_code) */
  defaultCurrencyForCountry(country: CountryCode): CurrencyCode | null;
};

function allCurrencyCodes(): string[] {
  return [...(Object.values(CurrencyCode) as string[])].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

function toEnumCurrency(code: string): CurrencyCode | null {
  const u = code.trim().toUpperCase();
  if ((Object.values(CurrencyCode) as string[]).includes(u)) {
    return u as CurrencyCode;
  }
  return null;
}

function fallbackCurrency(country: CountryCode): CurrencyCode {
  if (country === CountryCode.GH) {
    return CurrencyCode.GHS;
  }
  if (country === CountryCode.NG) {
    return CurrencyCode.NGN;
  }
  if (country === CountryCode.ZA) {
    return CurrencyCode.ZAR;
  }
  return CurrencyCode.GHS;
}

/**
 * Loads allowed currencies and per-country defaults from `organizationOperationCountries`.
 */
export async function fetchOrganizationCurrencies(
  _client: ApolloClient<object>,
  organizationId: string | null | undefined,
): Promise<OrganizationCurrencyInfo | null> {
  if (!organizationId) {
    return null;
  }
  // This repo does not expose org operating-country query/types in source.
  // Return all known enum currency codes so consumers can render a full list.
  const currencyCodesList = allCurrencyCodes();
  return {
    allowedCurrencyCodes: new Set(currencyCodesList),
    currencyCodesList,
    defaultCurrencyForCountry(country: CountryCode) {
      return fallbackCurrency(country);
    },
  };
}

/** When org currencies are unavailable, default currency for country (static map). */
export function defaultCurrencyFallback(country: CountryCode): CurrencyCode {
  return fallbackCurrency(country);
}

/** Distinct currency codes from tenant `operatingCountries` (Redux), sorted for templates. */
export function sortedCurrencyCodesFromOperatingCountryRows(
  rows: { currency_code?: string | null }[],
): string[] {
  const s = new Set<string>();
  for (const r of rows) {
    const c = r.currency_code?.trim().toUpperCase();
    if (c) {
      s.add(c);
    }
  }
  const fromRows = Array.from(s).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  return fromRows.length > 0 ? fromRows : allCurrencyCodes();
}

/**
 * Parse-time currency options from Redux `operatingCountries` (same shape as GraphQL rows).
 */
export function parseCurrencyOptionsFromOperatingCountryRows(
  rows: {
    iso_code?: string | null;
    country_code?: string | null;
    currency_code?: string | null;
  }[],
  defaultCountry: CountryCode,
): { defaultCurrency: CurrencyCode; allowedCurrencyCodes?: Set<string> } {
  const allowed = new Set<string>();
  for (const r of rows) {
    const c = r.currency_code?.trim().toUpperCase();
    if (c) {
      allowed.add(c);
    }
  }
  if (allowed.size === 0) {
    for (const c of allCurrencyCodes()) {
      allowed.add(c);
    }
  }
  const match =
    rows.find((r) => r.iso_code?.trim().toUpperCase() === defaultCountry) ??
    rows.find((r) => r.country_code?.trim().toUpperCase() === defaultCountry);
  const fromRow = toEnumCurrency(match?.currency_code ?? '') ?? null;
  return {
    defaultCurrency: fromRow ?? fallbackCurrency(defaultCountry),
    allowedCurrencyCodes: allowed,
  };
}
